// ================================================================
// 大師模式認證上下文
// ================================================================

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from 'react';
import { Teacher, teacherLogin, TeacherLoginRequest } from '../api/teacher';

export interface TeacherContextType {
  isTeacherAuthenticated: boolean;
  isLoading: boolean;
  teacher: Teacher | null;
  teacherToken: string | null;
  login: (data: TeacherLoginRequest) => Promise<void>;
  logout: () => void;
}

const TeacherContext = createContext<TeacherContextType | undefined>(undefined);

interface TeacherProviderProps {
  children: ReactNode;
}

export const TeacherProvider: React.FC<TeacherProviderProps> = ({ children }) => {
  const [isTeacherAuthenticated, setIsTeacherAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [teacher, setTeacher] = useState<Teacher | null>(null);
  const [teacherToken, setTeacherToken] = useState<string | null>(null);

  useEffect(() => {
    try {
      const storedToken = localStorage.getItem('teacher_token');
      const storedTeacher = localStorage.getItem('teacher_info');
      if (storedToken && storedTeacher) {
        setTeacherToken(storedToken);
        setTeacher(JSON.parse(storedTeacher));
        setIsTeacherAuthenticated(true);
      }
    } catch {
      localStorage.removeItem('teacher_token');
      localStorage.removeItem('teacher_info');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const login = useCallback(async (data: TeacherLoginRequest) => {
    const response = await teacherLogin(data);
    if (!response.success || !response.token || !response.teacher) {
      throw new Error(response.message || '登入失敗');
    }
    setTeacherToken(response.token);
    setTeacher(response.teacher);
    setIsTeacherAuthenticated(true);
    localStorage.setItem('teacher_token', response.token);
    localStorage.setItem('teacher_info', JSON.stringify(response.teacher));
  }, []);

  const logout = useCallback(() => {
    setTeacherToken(null);
    setTeacher(null);
    setIsTeacherAuthenticated(false);
    localStorage.removeItem('teacher_token');
    localStorage.removeItem('teacher_info');
  }, []);

  return (
    <TeacherContext.Provider
      value={{
        isTeacherAuthenticated,
        isLoading,
        teacher,
        teacherToken,
        login,
        logout,
      }}
    >
      {children}
    </TeacherContext.Provider>
  );
};

export const useTeacher = (): TeacherContextType => {
  const context = useContext(TeacherContext);
  if (!context) {
    throw new Error('useTeacher must be used within a TeacherProvider');
  }
  return context;
};

export { TeacherContext };
